import { useOrderLinks } from '~/adapters/services/accounts/orders/links';
import {
    AccountsPortalOrderState,
    getAccountsPortalOrderState,
} from '~/pages/OrderHistory/lib/getAccountsPortalOrderState';
import { OrderSummary } from '~/adapters/services/accounts/orders/types';
import { useFeatureFlagEnabled } from '~/adapters/signaler/hooks';
import { orderLinkTrackEvent } from '../../../tracking';

export interface ButtonConfig {
    type: 'status' | 'reorder' | 'design' | 'invoice';
    variant: 'primary' | 'secondary';
    order: number;
    isVisible: boolean;
    href: string;
    label: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const useOrderButtonConfigs = (order: OrderSummary, isImpersonatedExperience: boolean): ButtonConfig[] => {
    const reorderAddToCartEnabled = useFeatureFlagEnabled('reorder_add_to_cart_v1');
    const orderLinks = useOrderLinks(order, reorderAddToCartEnabled);

    const apOrderState = getAccountsPortalOrderState(order);
    const isOrderDelivered = apOrderState === AccountsPortalOrderState.Delivered; // false
    const isOrderCancelled = apOrderState === AccountsPortalOrderState.Cancelled; // false

    const isFixOrder = order.fixOrderId !== 0;
    const isShowReorderButton = isImpersonatedExperience || (!isOrderCancelled && !isFixOrder); // true

    const statusVariant: 'primary' | 'secondary' = !isOrderDelivered || !isShowReorderButton ? 'primary' : 'secondary';
    const reorderVariant: 'primary' | 'secondary' = !isOrderDelivered ? 'secondary' : 'primary';

    return [
        {
            type: 'status' as const,
            variant: statusVariant,
            order: isOrderDelivered ? 2 : 1,
            isVisible: true,
            href: orderLinks.trackOrderUrl,
            label: 'Order Status',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => orderLinkTrackEvent(event, 'order status'),
        },
        {
            type: 'reorder' as const,
            variant: reorderVariant,
            order: isOrderDelivered ? 1 : 2,
            isVisible: isShowReorderButton,
            href: orderLinks.reorderUrl,
            label: isImpersonatedExperience ? 'Internal Reorder' : 'Reorder',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => orderLinkTrackEvent(event, 'reorder'),
        },
        {
            type: 'design' as const,
            variant: 'secondary' as const,
            order: 3,
            isVisible: true,
            href: orderLinks.designUrl,
            label: isImpersonatedExperience ? 'Internal Checkout' : 'View Design',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => orderLinkTrackEvent(event, 'view design'),
        },
        {
            type: 'invoice' as const,
            variant: 'secondary' as const,
            order: 4,
            isVisible: true,
            href: orderLinks.invoiceUrl,
            label: 'View Invoice',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => orderLinkTrackEvent(event, 'view design invoice'),
        },
    ]
        .filter((button) => button.isVisible)
        .sort((a, b) => a.order - b.order);
};
